import { isLogicExpressionJson } from '@doss/common-all'
import { useEffect } from 'react'
import { useGetLogicExpressionJsonLazyQuery } from '~/common/generated/apollo-operations'
import { ConditionExpressionRenderer } from './ConditionExpressionRenderer'
import type { LookupFilterContextType } from './utils'
import { formatLogicExpressionForDisplay } from './utils'

interface ConditionExpressionProps {
  /** Logic expression string with optional {fieldId} placeholders. Rendered with field names and optional tooltips. */
  logicExpressionString?: string | null
  /** Map of field/column IDs to display names. Replaces {id} refs in the expression with these names. */
  fieldNameMap?: Record<string, string> | null
  /** Map of field/column IDs to context text shown on hover (e.g. "Table: Orders", "Form input"). */
  fieldContextNameMap?: Record<string, string> | null
  /** Map of field/column IDs to context type for color coding (form / currentTable / parentTable / childTable). */
  fieldContextTypeMap?: Record<string, LookupFilterContextType> | null
  /** Fallback to display the logic expression by loading the json version if no logic expression string exists */
  logicExpressionId: string
}

/**
 * Renders a logic expression string in view-only form. Uses fieldNameMap to show readable
 * names instead of IDs, fieldContextNameMap for hover tooltips, and fieldContextTypeMap for styling.
 */
export function ConditionExpression({
  logicExpressionString,
  fieldNameMap,
  fieldContextNameMap,
  fieldContextTypeMap,
  logicExpressionId,
}: ConditionExpressionProps) {
  const [getLogicExpressionJson, data] = useGetLogicExpressionJsonLazyQuery()

  useEffect(() => {
    if (!logicExpressionString) {
      getLogicExpressionJson({ variables: { logicExpressionId } })
    }
  }, [logicExpressionString, logicExpressionId, getLogicExpressionJson])

  if (!logicExpressionString) {
    const json = data.data?.logicExpressionJson
    if (!json || !isLogicExpressionJson(json)) {
      return null
    }
    return <ConditionExpressionRenderer data={json} />
  }

  const parts = formatLogicExpressionForDisplay(logicExpressionString, fieldNameMap ?? {}, {
    fieldContextNameMap: fieldContextNameMap ?? undefined,
    fieldContextTypeMap: fieldContextTypeMap ?? undefined,
  })

  return (
    <span className="font-mono text-xs leading-relaxed" data-testid="condition-expression-string">
      {parts}
    </span>
  )
}
